import { Model } from 'app/model/backendmodel';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { Owned } from 'app/model/base/named';
import { CopySink } from 'app/model/pipeline/copysink/copysink';
import { StoreWriteSettings } from 'app/model/pipeline/copysink/store-write-settings/store-write-settings';
import { Metamodel } from 'app/model/base/model';
import { merge } from 'lodash';
import { convertTypeToPlural } from 'app/model/pipeline/type.util';
import { AdvancedStoreWriteSettings } from 'app/model/pipeline/copysink/store-write-settings/advanced-store-write-settings';
import { BaseAsyncAdapter, ITridentResource, StoreSettingMetadata } from 'shell-models-lib';
import { arrayIsNullOrEmpty } from 'app/registries/utls/util';

export const storeWriteSettingsAdapts = {
    type: '*',
    maxConcurrentConnections: '*',
    copyBehavior: '*'
};

export abstract class StoreWriteSettingsAdapter<
    T extends StoreWriteSettings,
    U extends Model.StoreWriteSettings
> extends BaseAsyncAdapter<T, U> {
    public static toStoreWriteSettingsResource(model: StoreWriteSettings, resource: Model.StoreWriteSettings): void {
        if (model != null && resource != null) {
            resource.maxConcurrentConnections = ValueAdapter.toResource(
                model,
                StoreWriteSettings.MAX_CONCURRENT_CONNECTIONS
            );
            resource.copyBehavior = ValueAdapter.toResource(model, StoreWriteSettings.COPY_BEHAVIOR);
        }
    }

    public static toStoreWriteSettingsModel(resource: Model.StoreWriteSettings, model: StoreWriteSettings): void {
        if (model != null && resource != null) {
            ValueAdapter.toModel(
                model,
                StoreWriteSettings.MAX_CONCURRENT_CONNECTIONS,
                resource.maxConcurrentConnections
            );
            ValueAdapter.toModel(model, StoreWriteSettings.COPY_BEHAVIOR, resource.copyBehavior);
        }
    }

    protected override async _toResource(model: T): Promise<U> {
        const settingType = Metamodel.modelOf(model).name;
        const result: Model.StoreReadSettings = {
            type: settingType
        };

        if (model.raw) {
            const parsed = JSON.parse(model.raw);
            merge(result, parsed);
        }
        return result as U;
    }

    protected override async _toModel(resource: U, copySink: CopySink): Promise<T> {
        resource.type = convertTypeToPlural(resource.type);
        const resourceModel = Metamodel.modelByName(resource.type);
        const model = Owned.create<T>(resourceModel, copySink);

        const rawObject = {};
        merge(rawObject, await ValueAdapter.remainder(this.context, resource, resourceModel));
        model.raw = JSON.stringify(rawObject);
        return model;
    }

    protected _commonPropsToResource(result: Model.StoreWriteSettings, model: AdvancedStoreWriteSettings): void {
        if (!model || !result) {
            return;
        }
        result.blockSizeInMB = ValueAdapter.toResource(model, AdvancedStoreWriteSettings.BLOCK_SIZE);

        if (!arrayIsNullOrEmpty(model.metadata)) {
            result.metadata = [];
            model.metadata.forEach((metadatum) => {
                const value = ValueAdapter.toResource(metadatum, StoreSettingMetadata.VALUE) || '';
                result.metadata.push({
                    name: metadatum.name,
                    value
                });
            });
        }
    }

    protected _commonPropsToModel(
        resource: Model.StoreWriteSettings,
        result: AdvancedStoreWriteSettings,
        copySink: CopySink
    ): void {
        if (!resource || !result) {
            return;
        }
        ValueAdapter.toModel(result, AdvancedStoreWriteSettings.BLOCK_SIZE, resource.blockSizeInMB);
        if (!arrayIsNullOrEmpty(resource.metadata)) {
            result.metadata = [];
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            resource.metadata.forEach((datum: any) => {
                const modelColumn = Owned.create<StoreSettingMetadata>(StoreSettingMetadata.MODEL, copySink);
                modelColumn.name = datum.name;
                ValueAdapter.toModel(modelColumn, StoreSettingMetadata.VALUE, datum.value);
                result.metadata.push(modelColumn);
            });
        }
    }

    protected async _toAzureResource(tridentResource: ITridentResource): Promise<U> {
        throw `Artifact type '${tridentResource.artifactType}' does not implement _toAzureResource method.`;
    }
}
